import { t } from '@lingui/macro';
import { Trans } from '@lingui/macro';
import { motion } from 'motion/react';
import { FC, useContext, useState } from 'react';
import { Spinner } from 'react-activity';
import { toast } from 'sonner';

import { AppButton } from '@/UI';
import fetchData from '@/Utils/fetchData';
import { formatDate2 } from '@/Utils/formatDate';
import { deleteUserFromWhiteList } from '@/Utils/socket-actions/p2p-connection/p2pApis';
import { REQUEST_TYPE } from '@/constants/request-types';
import { useAppSelector } from '@/hooks/useRedux';

import classes from './P2PAdminPanel.module.css';
import { P2PContext } from './P2PContext';

interface IProps {}

const P2PPermittedUserDetails: FC<IProps> = () => {
    const [isSubmitting, setIsSubmitting] = useState(false);

    const context = useContext(P2PContext);
    const { setActiveUserItem, setUsersList, activeUserItem  } = context;

    const activeServer = useAppSelector(
        state => state?.serverDataReducer?.activeServer || undefined,
    );
    const expressURL =
        useAppSelector(
            state =>
                state.serverDataReducer.serverData?.[activeServer]?.chat_server
                    ?.express_url,
        ) || '';

    const url = expressURL + 'api/v1/logout';

    const logoutUser = async () => {
        const deviceId =
            activeUserItem && 'device_signed' in activeUserItem
                ? activeUserItem.device_signed
                : activeUserItem?.device_id;
        const body = {
            subserver_id: activeServer,
            device_id: deviceId,
            user_id: activeUserItem?.user_id,
        };
        const logoutRes = await fetchData(REQUEST_TYPE.POST, url, body);
        if (logoutRes?.data?.success) {
            toast.success(t`User logged out from the server`);
        } else {
            toast.error(logoutRes?.error || t`Error logging this user out.`);
        }
    };
    const revokeUser = async () => {
        setIsSubmitting(true);
        try {
            const { response, error } = await deleteUserFromWhiteList({
                permit_id: activeUserItem.id,
            });

            if (!error && response) {
                logoutUser();
                setUsersList(prev =>
                    prev.filter(el => el?.id !== activeUserItem?.id),
                );
                setActiveUserItem(null);
            } else {
                toast.error(
                    response ||
                        t`Error revoking your request. Please try again.`,
                );
            }
        } catch (err) {
            console.error(err);
            toast.error(t`Error revoking your request. Please try again.`);
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <motion.div
            initial={{ opacity: 0, x: 10 }}
            exit={{ opacity: 0, x: 10 }}
            animate={{ opacity: 1, x: 0 }}
            className={`${classes['body'] as string}`}>
            {activeUserItem && (
                <>
                    <table>
                        <tbody>
                            <tr>
                                <td>
                                    <Trans>Name</Trans>:{' '}
                                </td>
                                <td>{activeUserItem?.name}</td>
                            </tr>
                            <tr>
                                <td>
                                    <Trans>Username</Trans>:{' '}
                                </td>
                                <td>@{activeUserItem?.username}</td>
                            </tr>
                            <tr>
                                <td>
                                    <Trans>Device ID</Trans>:{' '}
                                </td>
                                <td>
                                    {activeUserItem &&
                                    'device_signed' in activeUserItem
                                        ? activeUserItem.device_signed
                                        : activeUserItem?.device_id}
                                </td>
                            </tr>
                            <tr>
                                <td>
                                    <Trans>ID</Trans>:{' '}
                                </td>
                                <td>{activeUserItem?.id}</td>
                            </tr>
                            <tr>
                                <td>
                                    <Trans>User ID</Trans>:{' '}
                                </td>
                                <td>{activeUserItem?.user_id}</td>
                            </tr>
                            <tr>
                                <td>
                                    <Trans>Subserver ID</Trans>:{' '}
                                </td>
                                <td>
                                    {'chat_subserver_id' in activeUserItem
                                        ? activeUserItem.chat_subserver_id
                                        : '--'}
                                </td>
                            </tr>

                            <tr>
                                <td>
                                    <Trans>User Hash</Trans>:{' '}
                                </td>
                                <td>
                                    {'hash_signed' in activeUserItem
                                        ? activeUserItem.hash_signed.sign
                                        : ''}
                                </td>
                            </tr>
                            <tr>
                                <td>
                                    <Trans>Date of Creation</Trans>:{' '}
                                </td>
                                <td>
                                    {formatDate2({
                                        date: new Date(
                                            activeUserItem?.created_at,
                                        ),
                                        returnTime: true,
                                    })}
                                </td>
                            </tr>
                        </tbody>
                    </table>
                    <div className={classes['flex']}>
                        <AppButton
                            disabled={isSubmitting}
                            onClick={revokeUser}
                            buttonType="danger"
                            title={
                                isSubmitting ? (
                                    <Spinner size={12} />
                                ) : activeUserItem.rejected ? (
                                    t`Clear`
                                ) : (
                                    t`Revoke`
                                )
                            }
                            classStyles={`${classes['details-btn']} ${classes['revoke-btn']}`}
                        />
                    </div>
                </>
            )}
        </motion.div>
    );
};
export default P2PPermittedUserDetails;
